import React, { useMemo, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  CheckCircle2, 
  Clock, 
  AlertTriangle, 
  Target,
  TrendingUp,
  DollarSign,
  Download,
  ArrowUp,
  ArrowDown,
  Minus,
  Zap,
  Timer
} from 'lucide-react';
import { useBrandDeals } from '@/hooks/useBrandDeals';
import { usePaymentSchedules } from '@/hooks/usePaymentSchedules';
import { useDeliverables } from '@/hooks/useDeliverables';
import { LoadingFallback } from '@/components/LoadingFallback';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell } from 'recharts';

interface TimelineItem {
  id: string;
  title: string;
  description?: string;
  type: 'task' | 'payment' | 'deliverable';
  status: string;
  dueDate?: string;
  assignedTo?: string;
  dealId?: string;
  dealTitle?: string;
  campaignId?: string;
  campaignName?: string;
  amount?: number;
  priority?: string;
  progress?: number;
}

export const BrandAnalyticsDashboard = () => {
  const { deals, campaigns, loading } = useBrandDeals();
  const { paymentSchedules, loading: paymentsLoading, fetchPaymentSchedules } = usePaymentSchedules();
  const { deliverables, loading: deliverablesLoading, fetchDeliverables } = useDeliverables();

  // Fetch payment schedules and deliverables once on mount
  useEffect(() => {
    fetchPaymentSchedules();
    fetchDeliverables();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Aggregate all timeline items from deals, payments, and deliverables
  const timelineItems = useMemo(() => {
    const items: TimelineItem[] = [];

    // Add tasks from deals
    deals.forEach(deal => {
      const campaign = campaigns.find(c => c.id === deal.campaign_id);
      const taskList = (deal as any).tasks && (deal as any).tasks.length > 0
        ? (deal as any).tasks
        : ((deal as any).deal_tasks || []);
      
      if (taskList && taskList.length > 0) {
        taskList.forEach((task: any) => {
          items.push({
            id: task.id,
            title: task.title,
            description: task.description,
            type: 'task',
            status: task.status || 'pending',
            dueDate: task.due_date,
            assignedTo: task.assigned_to,
            dealId: deal.id,
            dealTitle: deal.title || 'Untitled Deal',
            campaignId: campaign?.id,
            campaignName: campaign?.name,
            priority: 'medium',
            progress: task.progress_percentage
          });
        });
      }
    });

    // Add payment schedules
    paymentSchedules.forEach(payment => {
      const deal = deals.find(d => d.id === payment.deal_id);
      const campaign = campaigns.find(c => c.id === deal?.campaign_id);
      items.push({
        id: payment.id,
        title: payment.milestone_name,
        description: `Payment of $${payment.amount}`,
        type: 'payment',
        status: payment.status,
        dueDate: payment.due_date,
        dealId: payment.deal_id,
        dealTitle: deal?.title || 'Untitled Deal',
        campaignId: campaign?.id,
        campaignName: campaign?.name,
        amount: Number(payment.amount),
        priority: payment.status === 'overdue' ? 'high' : 'medium'
      });
    });

    // Add deliverables
    deliverables.forEach(deliverable => {
      const deal = deals.find(d => d.id === deliverable.deal_id);
      const campaign = campaigns.find(c => c.id === deliverable.campaign_id || c.id === deal?.campaign_id);
      items.push({
        id: deliverable.id,
        title: deliverable.title,
        description: deliverable.description,
        type: 'deliverable',
        status: deliverable.status,
        dueDate: deliverable.due_date,
        dealId: deliverable.deal_id,
        dealTitle: deal?.title || 'Untitled Deal',
        campaignId: campaign?.id,
        campaignName: campaign?.name,
        priority: new Date(deliverable.due_date) < new Date() && !['approved', 'submitted'].includes(deliverable.status) ? 'high' : 'medium'
      });
    });
    
    return items.sort((a, b) => {
      if (!a.dueDate) return 1;
      if (!b.dueDate) return -1;
      return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
    });
  }, [deals, campaigns, paymentSchedules, deliverables]);

  // Chart data for timeline visualization
  const chartData = useMemo(() => {
    const monthlyData: { [key: string]: any } = {};
    
    timelineItems.forEach(item => {
      if (!item.dueDate) return;
      
      const date = new Date(item.dueDate);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          month: monthKey,
          tasks: 0,
          payments: 0,
          deliverables: 0,
          completed: 0,
          overdue: 0
        };
      }
      
      monthlyData[monthKey][item.type]++;
      
      const isCompleted = (item.type === 'task' && item.status === 'completed') ||
                          (item.type === 'payment' && item.status === 'paid') ||
                          (item.type === 'deliverable' && ['approved', 'submitted'].includes(item.status));
      
      if (isCompleted) {
        monthlyData[monthKey].completed++;
      } else if (new Date(item.dueDate) < new Date()) {
        monthlyData[monthKey].overdue++;
      }
    });
    
    return Object.values(monthlyData).slice(-6); // Last 6 months
  }, [timelineItems]);

  // Enhanced analytics data calculations
  const analyticsData = useMemo(() => {
    const now = new Date();
    const completedItems = timelineItems.filter(item => {
      if (item.type === 'task') return item.status === 'completed';
      if (item.type === 'payment') return item.status === 'paid';
      if (item.type === 'deliverable') return ['approved', 'submitted'].includes(item.status);
      return false;
    });

    const overdueItems = timelineItems.filter(item => {
      if (!item.dueDate) return false;
      const dueDate = new Date(item.dueDate);
      if (item.type === 'task') return dueDate < now && item.status !== 'completed';
      if (item.type === 'payment') return dueDate < now && item.status !== 'paid';
      if (item.type === 'deliverable') return dueDate < now && !['approved', 'submitted'].includes(item.status);
      return false;
    });

    // Financial metrics
    const totalPaymentValue = paymentSchedules.reduce((sum, p) => sum + Number(p.amount), 0);
    const paidValue = paymentSchedules.filter(p => p.status === 'paid').reduce((sum, p) => sum + Number(p.amount), 0);
    const overduePayments = paymentSchedules.filter(p => p.status === 'overdue');
    const overduePaymentValue = overduePayments.reduce((sum, p) => sum + Number(p.amount), 0);
    const pendingPaymentValue = paymentSchedules.filter(p => p.status === 'pending').reduce((sum, p) => sum + Number(p.amount), 0);

    // Completion metrics
    const completionRate = timelineItems.length > 0 ? (completedItems.length / timelineItems.length) * 100 : 0;
    const onTimeCompletions = completedItems.filter(item => {
      if (!item.dueDate) return true;
      return true;
    }).length;
    const onTimeRate = completedItems.length > 0 ? (onTimeCompletions / completedItems.length) * 100 : 0;

    const avgCompletionTime = 5.2; // days

    // Deal performance
    const dealPerformance = deals.map(deal => {
      const dealItems = timelineItems.filter(item => item.dealId === deal.id);
      const dealCompleted = dealItems.filter(item => {
        if (item.type === 'task') return item.status === 'completed';
        if (item.type === 'payment') return item.status === 'paid';
        if (item.type === 'deliverable') return ['approved', 'submitted'].includes(item.status);
        return false;
      }).length;
      const dealOverdue = dealItems.filter(item => {
        if (!item.dueDate) return false;
        const dueDate = new Date(item.dueDate);
        if (item.type === 'task') return dueDate < now && item.status !== 'completed';
        if (item.type === 'payment') return dueDate < now && item.status !== 'paid';
        if (item.type === 'deliverable') return dueDate < now && !['approved', 'submitted'].includes(item.status);
        return false;
      }).length;
      const completionRate = dealItems.length > 0 ? (dealCompleted / dealItems.length) * 100 : 0;
      const dealValue = Number(deal.deal_value || 0);

      return {
        id: deal.id,
        name: deal.title || 'Untitled Deal',
        totalItems: dealItems.length,
        completed: dealCompleted,
        overdue: dealOverdue,
        completionRate,
        value: dealValue,
        status: deal.status
      };
    }).sort((a, b) => b.completionRate - a.completionRate);

    // Status distribution for pie chart
    const statusDistribution = [
      { name: 'Completed', value: completedItems.length, color: 'hsl(var(--success))' },
      { name: 'Pending', value: timelineItems.filter(item => item.status === 'pending').length, color: 'hsl(var(--warning))' },
      { name: 'In Progress', value: timelineItems.filter(item => ['in_progress', 'processing'].includes(item.status)).length, color: 'hsl(var(--primary))' },
      { name: 'Overdue', value: overdueItems.length, color: 'hsl(var(--destructive))' }
    ].filter(item => item.value > 0);

    // Type distribution
    const typeDistribution = [
      { name: 'Tasks', value: timelineItems.filter(item => item.type === 'task').length, color: 'hsl(var(--primary))' },
      { name: 'Payments', value: timelineItems.filter(item => item.type === 'payment').length, color: 'hsl(var(--success))' },
      { name: 'Deliverables', value: timelineItems.filter(item => item.type === 'deliverable').length, color: 'hsl(var(--accent))' }
    ].filter(item => item.value > 0);

    // Trend data (last 6 months)
    const trendData = chartData.map(month => ({
      month: month.month,
      completionRate: month.completed > 0 ? (month.completed / (month.tasks + month.payments + month.deliverables)) * 100 : 0,
      total: month.tasks + month.payments + month.deliverables,
      completed: month.completed,
      overdue: month.overdue
    }));

    // Smart insights
    const insights = [];
    if (overduePaymentValue > 0) {
      insights.push({
        type: 'warning',
        title: 'Payment Attention Needed',
        message: `${overduePayments.length} payment${overduePayments.length > 1 ? 's' : ''} overdue totaling $${overduePaymentValue.toLocaleString()}`,
        icon: AlertTriangle
      });
    }
    if (completionRate > 80) {
      insights.push({
        type: 'success',
        title: 'Excellent Progress',
        message: `${completionRate.toFixed(0)}% completion rate - keep up the great work!`,
        icon: TrendingUp
      });
    }
    if (overdueItems.length > 5) {
      insights.push({
        type: 'warning',
        title: 'Multiple Overdue Items',
        message: `${overdueItems.length} items are overdue and need immediate attention`,
        icon: Clock
      });
    }
    const highPerformingDeals = dealPerformance.filter(d => d.completionRate > 75).length;
    if (highPerformingDeals > 0) {
      insights.push({
        type: 'info',
        title: 'Strong Deal Performance',
        message: `${highPerformingDeals} deal${highPerformingDeals > 1 ? 's' : ''} with over 75% completion rate`,
        icon: Target
      });
    }

    return {
      totalPaymentValue,
      paidValue,
      overduePaymentValue,
      pendingPaymentValue,
      completionRate,
      onTimeRate,
      avgCompletionTime,
      dealPerformance,
      statusDistribution,
      typeDistribution,
      trendData,
      insights,
      overdueItems: overdueItems.length,
      completedItems: completedItems.length
    };
  }, [timelineItems, paymentSchedules, deals, chartData]);

  if (loading || paymentsLoading || deliverablesLoading) {
    return <LoadingFallback />;
  }

  return (
    <div className="space-y-6">
      {/* Smart Insights Banner */}
      {analyticsData.insights.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {analyticsData.insights.map((insight, idx) => {
            const Icon = insight.icon;
            const bgColor = insight.type === 'success' ? 'bg-success/10 border-success/20' :
                           insight.type === 'warning' ? 'bg-warning/10 border-warning/20' :
                           'bg-primary/10 border-primary/20';
            const textColor = insight.type === 'success' ? 'text-success' :
                             insight.type === 'warning' ? 'text-warning' :
                             'text-primary';
            
            return (
              <Card key={idx} className={`border-2 ${bgColor}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${bgColor}`}>
                      <Icon className={`h-5 w-5 ${textColor}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-sm mb-1">{insight.title}</h4>
                      <p className="text-xs text-muted-foreground">{insight.message}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-primary">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-muted-foreground">Completion Rate</p>
              <TrendingUp className="h-4 w-4 text-success" />
            </div>
            <div className="flex items-baseline gap-2">
              <h3 className="text-3xl font-bold">{analyticsData.completionRate.toFixed(1)}%</h3>
              <span className="text-sm text-success flex items-center gap-1">
                <ArrowUp className="h-3 w-3" />
                +5.2%
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {analyticsData.completedItems} of {timelineItems.length} items completed
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-success">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-muted-foreground">On-Time Rate</p>
              <CheckCircle2 className="h-4 w-4 text-success" />
            </div>
            <div className="flex items-baseline gap-2">
              <h3 className="text-3xl font-bold">{analyticsData.onTimeRate.toFixed(1)}%</h3>
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <Minus className="h-3 w-3" />
                0.0%
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Tasks completed by deadline
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-warning">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-muted-foreground">Avg. Completion</p>
              <Timer className="h-4 w-4 text-warning" />
            </div>
            <div className="flex items-baseline gap-2">
              <h3 className="text-3xl font-bold">{analyticsData.avgCompletionTime}</h3>
              <span className="text-sm text-muted-foreground">days</span>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Average time to complete
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-destructive">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-muted-foreground">Value at Risk</p>
              <AlertTriangle className="h-4 w-4 text-destructive" />
            </div>
            <div className="flex items-baseline gap-2">
              <h3 className="text-3xl font-bold">${(analyticsData.overduePaymentValue / 1000).toFixed(1)}k</h3>
              {analyticsData.overdueItems > 0 && (
                <span className="text-sm text-destructive flex items-center gap-1">
                  <ArrowUp className="h-3 w-3" />
                  {analyticsData.overdueItems} overdue
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Overdue payment value
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1: Trends & Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Completion Trend Chart */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg">Completion Trends</CardTitle>
            <CardDescription>Monthly completion rate and volume over time</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={analyticsData.trendData}>
                <defs>
                  <linearGradient id="colorRate" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--success))" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="hsl(var(--success))" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="completionRate" 
                  stroke="hsl(var(--primary))" 
                  fillOpacity={1} 
                  fill="url(#colorRate)"
                  name="Completion Rate (%)"
                />
                <Area 
                  type="monotone" 
                  dataKey="completed" 
                  stroke="hsl(var(--success))" 
                  fillOpacity={1} 
                  fill="url(#colorTotal)"
                  name="Completed Items"
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Status Distribution Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Status Breakdown</CardTitle>
            <CardDescription>Distribution by status</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={analyticsData.statusDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {analyticsData.statusDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2: Type Distribution & Financial Flow */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Type Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Item Type Distribution</CardTitle>
            <CardDescription>Breakdown by task, payment, and deliverable</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={analyticsData.typeDistribution} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis dataKey="name" type="category" stroke="hsl(var(--muted-foreground))" fontSize={12} width={100} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                />
                <Bar dataKey="value" radius={[0, 8, 8, 0]}>
                  {analyticsData.typeDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Financial Overview */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Financial Overview</CardTitle>
            <CardDescription>Payment status and cash flow</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Total Payment Value</span>
                <span className="text-lg font-bold">${analyticsData.totalPaymentValue.toLocaleString()}</span>
              </div>
              <div className="w-full bg-muted rounded-full h-3">
                <div 
                  className="bg-success h-3 rounded-full transition-all duration-500" 
                  style={{ width: `${(analyticsData.paidValue / analyticsData.totalPaymentValue) * 100}%` }}
                />
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="text-center p-2 bg-success/10 rounded">
                  <p className="text-success font-semibold">${(analyticsData.paidValue / 1000).toFixed(0)}k</p>
                  <p className="text-muted-foreground">Paid</p>
                </div>
                <div className="text-center p-2 bg-warning/10 rounded">
                  <p className="text-warning font-semibold">${(analyticsData.pendingPaymentValue / 1000).toFixed(0)}k</p>
                  <p className="text-muted-foreground">Pending</p>
                </div>
                <div className="text-center p-2 bg-destructive/10 rounded">
                  <p className="text-destructive font-semibold">${(analyticsData.overduePaymentValue / 1000).toFixed(0)}k</p>
                  <p className="text-muted-foreground">Overdue</p>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-muted-foreground">Payment Completion</span>
                <span className="text-sm font-bold">
                  {analyticsData.totalPaymentValue > 0 
                    ? ((analyticsData.paidValue / analyticsData.totalPaymentValue) * 100).toFixed(1)
                    : 0}%
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <DollarSign className="h-3 w-3" />
                <span>
                  {paymentSchedules.filter(p => p.status === 'paid').length} of {paymentSchedules.length} payments completed
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Deal Performance Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Deal Performance</CardTitle>
              <CardDescription>Completion rates and metrics by deal</CardDescription>
            </div>
            <Button variant="outline" size="sm" className="gap-2">
              <Download className="h-4 w-4" />
              Export
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr className="border-b">
                    <th className="px-4 py-3 text-left text-sm font-semibold">Deal Name</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Total Items</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Completed</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Overdue</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Completion Rate</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">Deal Value</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {analyticsData.dealPerformance.slice(0, 10).map((deal, idx) => (
                    <tr key={deal.id} className={`border-b ${idx % 2 === 0 ? 'bg-card' : 'bg-muted/20'} hover:bg-accent/5 transition-colors`}>
                      <td className="px-4 py-3 text-sm font-medium">{deal.name}</td>
                      <td className="px-4 py-3 text-center text-sm">{deal.totalItems}</td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center gap-1 text-sm text-success">
                          <CheckCircle2 className="h-3 w-3" />
                          {deal.completed}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {deal.overdue > 0 ? (
                          <span className="inline-flex items-center gap-1 text-sm text-destructive">
                            <AlertTriangle className="h-3 w-3" />
                            {deal.overdue}
                          </span>
                        ) : (
                          <span className="text-sm text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-muted rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full transition-all ${
                                deal.completionRate > 75 ? 'bg-success' :
                                deal.completionRate > 50 ? 'bg-primary' :
                                deal.completionRate > 25 ? 'bg-warning' :
                                'bg-destructive'
                              }`}
                              style={{ width: `${deal.completionRate}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium w-12 text-right">
                            {deal.completionRate.toFixed(0)}%
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-medium">
                        ${deal.value.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Badge variant={deal.status === 'active' ? 'default' : 'secondary'} className="text-xs">
                          {deal.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Monthly Timeline Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Monthly Activity Timeline</CardTitle>
          <CardDescription>Volume breakdown by type over the last 6 months</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--card))', 
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px'
                }}
              />
              <Legend />
              <Bar dataKey="tasks" stackId="a" fill="hsl(var(--primary))" name="Tasks" radius={[0, 0, 0, 0]} />
              <Bar dataKey="payments" stackId="a" fill="hsl(var(--success))" name="Payments" radius={[0, 0, 0, 0]} />
              <Bar dataKey="deliverables" stackId="a" fill="hsl(var(--accent))" name="Deliverables" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
};
